import {
  objectIdSchema,
  paginationWithSearchSchema,
  partyRoleSchema,
  Profile,
  ProfileSchema,
  profileSourceSchema,
} from '@hero/schema';
import { z } from 'zod';
import { API } from '..';

const schema = ProfileSchema.model;

/**
 * `Profile` collection — global, admin-curated brand directory.
 * Writes are super-admin only; reads are open to any signed-in Org.
 * See `docs/relations-v2/02-data-model.md` section 7.
 */

//* read (any signed-in user)
export const profileGetAllAPISchema = paginationWithSearchSchema.extend({
  /** Optional: filter by role. */
  role: partyRoleSchema.optional(),
  /**
   * Optional: filter by provenance (`admin` / `csv_import` /
   * `suggested`). The admin Brand Directory list passes
   * `source: 'suggested'` to triage tenant-submitted suggestions
   * before promoting them to `admin` via update.
   */
  source: profileSourceSchema.optional(),
});
export type ProfileGetAllAPI = API<z.infer<typeof profileGetAllAPISchema>, Profile[]>;

export const profileGetOneAPISchema = schema.pick({ _id: true });
export type ProfileGetOneAPI = API<z.infer<typeof profileGetOneAPISchema>, Profile>;

//* admin CRUD
export const profileCreateAdminAPISchema = z.object({
  name: schema.shape.name,
  legalName: schema.shape.name.optional(),
  registrationId: z.string().optional(),
  aliases: z.array(z.string()).default([]),
  linkedOrgId: objectIdSchema.optional(),
  roles: z.array(partyRoleSchema).min(1),
});
export type ProfileCreateAdminAPI = API<
  z.infer<typeof profileCreateAdminAPISchema>,
  Profile
>;

/**
 * Tenant-facing "suggest a new brand" endpoint. Backs the
 * `EntityDirectoryPicker` bottom-row affordance: any signed-in
 * caller may CREATE a Profile, but the server pins
 * `source: 'suggested'` and admins still own updates / deletes.
 *
 * Narrow ADR #10 carve-out: tenants may CREATE a Profile from this
 * flow only — full directory admin CRUD remains super-admin scoped
 * (see {@link profileCreateAdminAPISchema}).
 */
export const profileSuggestAPISchema = z.object({
  name: schema.shape.name,
  aliases: z.array(z.string()).default([]),
  roles: z.array(partyRoleSchema).min(1),
});
export type ProfileSuggestAPI = API<z.infer<typeof profileSuggestAPISchema>, Profile>;

export const profileUpdateAdminAPISchema = profileCreateAdminAPISchema
  .partial()
  .extend({ _id: objectIdSchema });
export type ProfileUpdateAdminAPI = API<
  z.infer<typeof profileUpdateAdminAPISchema>,
  Profile
>;

export const profileDeleteAdminAPISchema = z.object({ _id: objectIdSchema });
export type ProfileDeleteAdminAPI = API<
  z.infer<typeof profileDeleteAdminAPISchema>,
  undefined
>;

//* bulk import — admin
export const profileBulkImportAdminAPISchema = z.object({
  rows: z.array(
    z.object({
      name: z.string(),
      legalName: z.string().optional(),
      registrationId: z.string().optional(),
      aliases: z.array(z.string()).optional(),
      roles: z.array(partyRoleSchema).min(1),
    }),
  ),
});
export type ProfileBulkImportAdminAPI = API<
  z.infer<typeof profileBulkImportAdminAPISchema>,
  {
    created: number;
    skipped: number;
    errors: Array<{ row: number; error: string }>;
  }
>;
